// حساب مواقيت الصلاة فلكياً عبر مكتبة adhan.
//
// سابقاً كانت المواقيت نصوصاً مكتوبة يدوياً لكل مدينة ("05:08" لمكة مثلاً)
// لا تتغير مع اليوم ولا الشهر ولا الموقع، ومسار الـ GPS كان يخزّن نتيجة
// الـ API في localStorage بلا تاريخ فتبقى مواقيت اليوم الأول للأبد.
//
// الآن: حساب حقيقي، يعمل دون إنترنت، لأي تاريخ وأي إحداثيات.

import {
  CalculationMethod,
  Coordinates,
  PrayerTimes,
  Qibla,
  SunnahTimes,
  Madhab,
} from 'adhan';

/** طرق الحساب المعتمدة في العالم الإسلامي. */
export const CALCULATION_METHODS = [
  { id: 'UmmAlQura', label: 'أم القرى (مكة المكرمة)', build: () => CalculationMethod.UmmAlQura() },
  { id: 'Egyptian', label: 'الهيئة المصرية العامة للمساحة', build: () => CalculationMethod.Egyptian() },
  { id: 'MuslimWorldLeague', label: 'رابطة العالم الإسلامي', build: () => CalculationMethod.MuslimWorldLeague() },
  { id: 'Karachi', label: 'جامعة العلوم الإسلامية – كراتشي', build: () => CalculationMethod.Karachi() },
  { id: 'Dubai', label: 'دائرة الشؤون الإسلامية – دبي', build: () => CalculationMethod.Dubai() },
  { id: 'Qatar', label: 'مؤسسة قطر', build: () => CalculationMethod.Qatar() },
  { id: 'Kuwait', label: 'وزارة الأوقاف الكويتية', build: () => CalculationMethod.Kuwait() },
  { id: 'Turkey', label: 'رئاسة الشؤون الدينية – تركيا', build: () => CalculationMethod.Turkey() },
  { id: 'NorthAmerica', label: 'ISNA – أمريكا الشمالية', build: () => CalculationMethod.NorthAmerica() },
];

export const DEFAULT_METHOD_ID = 'UmmAlQura';

function buildParams(methodId, madhab) {
  const entry =
    CALCULATION_METHODS.find((m) => m.id === methodId) ??
    CALCULATION_METHODS.find((m) => m.id === DEFAULT_METHOD_ID);

  const params = entry.build();
  // الحنفي يؤخّر العصر إلى مثليّ الظل
  params.madhab = madhab === 'hanafi' ? Madhab.Hanafi : Madhab.Shafi;
  return params;
}

const pad = (n) => String(n).padStart(2, '0');

// ذاكرة للمنسّقات — إنشاء Intl.DateTimeFormat مكلف نسبياً وتُستدعى كل ثانية
const formatterCache = new Map();

function getFormatter(timeZone) {
  const key = timeZone ?? 'local';
  let fmt = formatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...(timeZone ? { timeZone } : {}),
    });
    formatterCache.set(key, fmt);
  }
  return fmt;
}

/**
 * تنسيق 24 ساعة في منطقة زمنية محددة.
 *
 * `timeZone` ضروري: getHours() يعرض بتوقيت الجهاز، فمن يختار مكة وهو في
 * منطقة أخرى كان يرى مواقيت مكة محوَّلة إلى توقيته — الفجر 01:53 بدل 04:53.
 * المطلوب أن تُعرض مواقيت المدينة المختارة بتوقيتها هي.
 */
export const formatTime = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '--:--';
  try {
    return getFormatter(timeZone).format(date);
  } catch {
    // منطقة زمنية غير معروفة — نتراجع إلى توقيت الجهاز
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
};

/**
 * يبني تاريخاً يوافق يومَ التقويم في المنطقة الزمنية المطلوبة.
 *
 * مكتبة adhan تشتقّ اليوم من التوقيت المحلي للـ Date الممرَّر. فإن كان
 * الجهاز على UTC والمدينة على UTC+3، فعند الساعة 22:00 بتوقيت الجهاز تكون
 * المدينة قد دخلت اليوم التالي — وكانت المواقيت تُحسب لليوم الخطأ.
 */
function calendarDateIn(timeZone, at = new Date()) {
  if (!timeZone) return at;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(at);

    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    // الظهر محلياً يضمن البقاء داخل اليوم مهما كان فرق التوقيت
    return new Date(get('year'), get('month') - 1, get('day'), 12, 0, 0);
  } catch {
    return at;
  }
}

/** اليوم في منطقة زمنية، كنص YYYY-MM-DD — يُستخدم لمقارنة الأيام. */
function dayKeyIn(timeZone, at) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at);
  } catch {
    return at.toDateString();
  }
}

/**
 * مواقيت يوم كامل لإحداثيات معيّنة.
 * `times` لحظات مطلقة (Date)، و`formatted` نصوص في منطقة `timeZone`.
 * @returns {{ times: Record<string, Date>, formatted: Record<string, string>, sunnah: object }}
 */
export function getPrayerTimes({ lat, lng, date = new Date(), methodId = DEFAULT_METHOD_ID, madhab = 'shafi', timeZone }) {
  const coordinates = new Coordinates(lat, lng);
  const params = buildParams(methodId, madhab);
  const pt = new PrayerTimes(coordinates, calendarDateIn(timeZone, date), params);
  const sunnah = new SunnahTimes(pt);

  const times = {
    fajr: pt.fajr,
    sunrise: pt.sunrise,
    dhuhr: pt.dhuhr,
    asr: pt.asr,
    maghrib: pt.maghrib,
    isha: pt.isha,
  };

  const formatted = Object.fromEntries(
    Object.entries(times).map(([key, value]) => [key, formatTime(value, timeZone)])
  );

  return {
    times,
    formatted,
    raw: pt,
    sunnah: {
      middleOfTheNight: sunnah.middleOfTheNight,
      lastThirdOfTheNight: sunnah.lastThirdOfTheNight,
    },
  };
}

const PRAYER_LABELS = {
  fajr: 'الفجر',
  sunrise: 'الشروق',
  dhuhr: 'الظهر',
  asr: 'العصر',
  maghrib: 'المغرب',
  isha: 'العشاء',
};

export const prayerLabel = (key) => PRAYER_LABELS[key] ?? key;

/**
 * الصلاة القادمة والوقت المتبقي لها — يعبر منتصف الليل بشكل صحيح
 * بالرجوع إلى فجر اليوم التالي بدل جمع 24 ساعة يدوياً.
 */
export function getNextPrayer({ lat, lng, now = new Date(), methodId, madhab, timeZone }) {
  const coordinates = new Coordinates(lat, lng);
  const params = buildParams(methodId, madhab);

  const today = new PrayerTimes(coordinates, calendarDateIn(timeZone, now), params);

  // الشروق ليس صلاة، لذا نستبعده من العدّ التنازلي
  const order = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

  let nextKey = null;
  let nextDate = null;

  for (const key of order) {
    const t = today[key];
    if (t instanceof Date && t.getTime() > now.getTime()) {
      nextKey = key;
      nextDate = t;
      break;
    }
  }

  // بعد العشاء → فجر الغد
  if (!nextDate) {
    const base = calendarDateIn(timeZone, now);
    const tomorrow = new Date(base);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const next = new PrayerTimes(coordinates, tomorrow, params);
    nextKey = 'fajr';
    nextDate = next.fajr;
  }

  const diffMs = Math.max(0, nextDate.getTime() - now.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    key: nextKey,
    name: prayerLabel(nextKey),
    at: nextDate,
    // المقارنة في منطقة المدينة لا في توقيت الجهاز
    isTomorrow: dayKeyIn(timeZone, nextDate) !== dayKeyIn(timeZone, now),
    totalSeconds,
    countdown: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
  };
}

/** الصلاة الحالية (التي دخل وقتها ولم يدخل ما بعدها). */
export function getCurrentPrayer({ lat, lng, now = new Date(), methodId, madhab, timeZone }) {
  const pt = new PrayerTimes(
    new Coordinates(lat, lng),
    calendarDateIn(timeZone, now),
    buildParams(methodId, madhab)
  );
  const key = pt.currentPrayer(now);
  return key && key !== 'none' ? { key, name: prayerLabel(key) } : null;
}

/** زاوية القبلة من الشمال — من المكتبة نفسها بدل حساب يدوي. */
export function getQiblaDirection(lat, lng) {
  return Qibla(new Coordinates(lat, lng));
}

const KAABA = { lat: 21.4224779, lng: 39.8251832 };

/** المسافة إلى الكعبة بالكيلومترات (Haversine). */
export function getDistanceToKaaba(lat, lng) {
  const R = 6371;
  const dLat = ((KAABA.lat - lat) * Math.PI) / 180;
  const dLon = ((KAABA.lng - lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat * Math.PI) / 180) * Math.cos((KAABA.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}
